// Per-device cache of issued raw tokens, keyed by token_id.
//
// The server only ever sees the raw token at issuance — it stores a
// SHA-256 hash and forgets the rest. That's intentional: a leaked
// PG dump can't be replayed against the gateway. But it means the
// dashboard must persist the raw secret itself if we want to render
// concrete URLs in the token detail panel after issuance.
//
// Same security posture as the device privkey (lib/identity.ts):
// stays on this device, gets cleared on signout. Device B never had
// the secret to begin with — TokenDetailPanel falls back to the
// `<your-token>` placeholder there.

import { openDb } from './identity';

const STORE = 'raw_tokens';

/** Persist `raw_token` keyed by `token_id`. Idempotent overwrites. */
export async function saveRawToken(token_id: string, raw_token: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(raw_token, token_id);
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error ?? new Error('indexedDB put failed'));
    });
    db.close();
  } catch (err) {
    // Best-effort: if IndexedDB is unavailable (private browsing in
    // some contexts, storage quota), the customer just sees the
    // placeholder URL. Not a failure mode worth surfacing.
    console.warn('token-cache: saveRawToken failed', err);
  }
}

/** Returns the raw token if cached on this device, else null. */
export async function loadRawToken(token_id: string): Promise<string | null> {
  try {
    const db = await openDb();
    const v = await new Promise<string | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(token_id);
      req.onsuccess = () => resolve(req.result as string | undefined);
      req.onerror   = () => reject(req.error ?? new Error('indexedDB get failed'));
    });
    db.close();
    return v ?? null;
  } catch (err) {
    console.warn('token-cache: loadRawToken failed', err);
    return null;
  }
}

/** Clear a single token's cached raw value. Called on revoke so the
 *  detail panel doesn't keep showing a working URL for a dead token. */
export async function clearRawToken(token_id: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(token_id);
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error ?? new Error('indexedDB delete failed'));
    });
    db.close();
  } catch (err) {
    console.warn('token-cache: clearRawToken failed', err);
  }
}
